import { createContext, useContext, useState, useEffect } from 'react';

const SettingsContext = createContext();

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within SettingsProvider');
  }
  return context;
}

export function SettingsProvider({ children }) {
  const [currency, setCurrency] = useState(() => {
    return localStorage.getItem('pedalscot_currency') || 'EUR';
  });
  
  const [units, setUnits] = useState(() => {
    return localStorage.getItem('pedalscot_units') || 'metric';
  });

  const [exchangeRate, setExchangeRate] = useState(0.86); // EUR to GBP default

  useEffect(() => {
    // Fetch current exchange rate
    const fetchExchangeRate = async () => {
      try {
        const response = await fetch('https://api.exchangerate-api.com/v4/latest/EUR');
        const data = await response.json();
        setExchangeRate(data.rates.GBP);
      } catch (error) {
        console.error('Failed to fetch exchange rate:', error);
        // Fallback to default
        setExchangeRate(0.86);
      }
    };
    
    if (currency === 'GBP') {
      fetchExchangeRate();
    }
  }, [currency]);

  const toggleCurrency = () => {
    const newCurrency = currency === 'EUR' ? 'GBP' : 'EUR';
    setCurrency(newCurrency);
    localStorage.setItem('pedalscot_currency', newCurrency);
  };

  const toggleUnits = () => {
    const newUnits = units === 'metric' ? 'imperial' : 'metric';
    setUnits(newUnits);
    localStorage.setItem('pedalscot_units', newUnits);
  };

  const formatPrice = (priceEUR) => {
    if (currency === 'GBP') {
      const priceGBP = (priceEUR * exchangeRate).toFixed(2);
      return `£${priceGBP}`;
    }
    return `${priceEUR}€`;
  };

  const formatDistance = (distanceKm) => {
    if (units === 'imperial') {
      const miles = (distanceKm * 0.621371).toFixed(1);
      return `${miles} mi`;
    }
    return `${distanceKm} km`;
  };

  const formatElevation = (elevationM) => {
    if (units === 'imperial') {
      const feet = Math.round(elevationM * 3.28084);
      return `${feet} ft`;
    }
    return `${elevationM} m`;
  };

  const formatSpeed = (speedKmh) => {
    if (units === 'imperial') {
      const mph = (speedKmh * 0.621371).toFixed(1);
      return `${mph} mph`;
    }
    return `${speedKmh} km/h`;
  };

  const value = {
    currency,
    units,
    exchangeRate,
    toggleCurrency,
    toggleUnits,
    formatPrice,
    formatDistance,
    formatElevation,
    formatSpeed,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}
